import React, { useState, useEffect, useRef, useCallback } from 'react';
import { api } from '../services/api';
import ConfirmModal from './ConfirmModal';

// Compress image to max 1200px, 80% JPEG for upload
function compressImage(file) {
  return new Promise((resolve) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      const img = new Image();
      img.onload = () => {
        const MAX = 1200;
        let { width, height } = img;
        if (width > MAX || height > MAX) {
          if (width > height) { height = Math.round(height * MAX / width); width = MAX; }
          else { width = Math.round(width * MAX / height); height = MAX; }
        }
        const canvas = document.createElement('canvas');
        canvas.width = width; canvas.height = height;
        canvas.getContext('2d').drawImage(img, 0, 0, width, height);
        resolve(canvas.toDataURL('image/jpeg', 0.82));
      };
      img.src = e.target.result;
    };
    reader.readAsDataURL(file);
  });
}

const CATEGORY_COLORS = {
  Starters: '#8b5cf6', Mains: '#3b82f6', Desserts: '#ec4899',
  Drinks: '#10b981', Sides: '#f59e0b', General: '#64748b',
};
function categoryColor(cat) {
  return CATEGORY_COLORS[cat] || '#4f46e5';
}

export default function Menu() {
  const [items, setItems]             = useState([]);
  const [loading, setLoading]         = useState(true);
  const [scanning, setScanning]       = useState(false);
  const [scanResults, setScanResults] = useState(null);
  const [previewImg, setPreviewImg]   = useState(null);
  const [error, setError]             = useState('');
  const [editItem, setEditItem]       = useState(null);
  const [addItem, setAddItem]         = useState(null); // null = closed, {} = open
  const [activeCategory, setActiveCategory] = useState('All');
  const [deleteConfirm, setDeleteConfirm]   = useState(null);
  const [clearConfirm, setClearConfirm]     = useState(false);
  const fileRef = useRef();

  const BLANK_ITEM = { name: '', category: '', price: '', description: '', is_available: true };

  const load = useCallback(async () => {
    try { const data = await api.getMenu(); setItems(data); }
    catch (_) {}
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const categories = ['All', ...Array.from(new Set(items.map(i => i.category))).sort()];
  const visible = activeCategory === 'All' ? items : items.filter(i => i.category === activeCategory);

  // ── File pick / camera ─────────────────────────────────────────────────────
  const handleFileChange = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    e.target.value = '';
    setError('');
    setScanning(true);
    setScanResults(null);
    try {
      const compressed = await compressImage(file);
      setPreviewImg(compressed);
      const { items: extracted } = await api.scanMenu(compressed);
      if (!extracted || extracted.length === 0) {
        setError("Couldn't read any items from that image. Try a clearer photo.");
        setScanning(false);
        setPreviewImg(null);
        return;
      }
      setScanResults(extracted);
    } catch (err) {
      setError(err.message || 'Scan failed. Please try again.');
      setPreviewImg(null);
    } finally {
      setScanning(false);
    }
  };

  // ── Confirm scan results ───────────────────────────────────────────────────
  const handleConfirmScan = async () => {
    if (!scanResults) return;
    try {
      await api.bulkCreateMenu(scanResults);
      setScanResults(null);
      setPreviewImg(null);
      await load();
    } catch (err) {
      setError(err.message || 'Failed to save items.');
    }
  };

  const handleRemoveScanItem = (idx) => {
    setScanResults(prev => prev.filter((_, i) => i !== idx));
  };

  const handleEditScanItem = (idx, field, val) => {
    setScanResults(prev => prev.map((item, i) => i === idx ? { ...item, [field]: val } : item));
  };

  // ── Edit saved item ────────────────────────────────────────────────────────
  const handleSaveEdit = async () => {
    if (!editItem) return;
    try {
      await api.updateMenuItem(editItem.id, {
        name: editItem.name, category: editItem.category,
        price: editItem.price, description: editItem.description,
        is_available: editItem.is_available,
      });
      setEditItem(null);
      await load();
    } catch (_) {}
  };

  // ── Add manually ──────────────────────────────────────────────────────────
  const handleSaveAdd = async () => {
    if (!addItem || !addItem.name.trim()) return;
    try {
      await api.createMenuItem({
        name: addItem.name.trim(),
        category: addItem.category.trim() || 'General',
        price: addItem.price.trim(),
        description: addItem.description.trim(),
        is_available: addItem.is_available,
      });
      setAddItem(null);
      await load();
    } catch (_) {}
  };

  // ── Delete ─────────────────────────────────────────────────────────────────
  const handleDelete = async (id) => {
    try { await api.deleteMenuItem(id); setDeleteConfirm(null); await load(); } catch (_) {}
  };

  const handleClearAll = () => setClearConfirm(true);
  const doClearAll = async () => {
    setClearConfirm(false);
    try { await api.clearMenu(); await load(); } catch (_) {}
  };

  if (loading) return <div style={s.empty}>Loading…</div>;

  // ── Scan review screen ────────────────────────────────────────────────────
  if (scanResults !== null) {
    return (
      <div style={s.page}>
        <div style={s.header}>
          <div>
            <h1 style={s.title}>Review extracted items</h1>
            <p style={s.sub}>The AI found {scanResults.length} item{scanResults.length !== 1 ? 's' : ''}. Edit or remove any before saving.</p>
          </div>
          <div style={{ display: 'flex', gap: 10 }}>
            <button style={s.btnGhost} onClick={() => { setScanResults(null); setPreviewImg(null); }}>Cancel</button>
            <button style={s.btnPrimary} onClick={handleConfirmScan} disabled={scanResults.length === 0}>
              Save {scanResults.length} item{scanResults.length !== 1 ? 's' : ''} →
            </button>
          </div>
        </div>

        {previewImg && (
          <img src={previewImg} alt="Scanned menu" style={s.previewImg} />
        )}

        <div style={s.reviewList}>
          {scanResults.map((item, idx) => (
            <div key={idx} style={s.reviewRow}>
              <div style={s.reviewFields}>
                <input style={{ ...s.reviewInput, fontWeight: 600 }} value={item.name}
                  onChange={e => handleEditScanItem(idx, 'name', e.target.value)} placeholder="Item name" />
                <input style={s.reviewInput} value={item.category}
                  onChange={e => handleEditScanItem(idx, 'category', e.target.value)} placeholder="Category" />
                <input style={{ ...s.reviewInput, width: 100 }} value={item.price}
                  onChange={e => handleEditScanItem(idx, 'price', e.target.value)} placeholder="Price" />
                <input style={{ ...s.reviewInput, flex: 2 }} value={item.description}
                  onChange={e => handleEditScanItem(idx, 'description', e.target.value)} placeholder="Description" />
              </div>
              <button style={s.removeBtn} onClick={() => handleRemoveScanItem(idx)} title="Remove">×</button>
            </div>
          ))}
        </div>
      </div>
    );
  }

  // ── Main menu view ─────────────────────────────────────────────────────────
  return (
    <div style={s.page}>
      <div style={s.header}>
        <div>
          <h1 style={s.title}>Menu</h1>
          <p style={s.sub}>
            {items.length === 0
              ? 'Upload a photo of your menu — AI will extract all items automatically.'
              : `${items.length} item${items.length !== 1 ? 's' : ''} · used by AI to answer dietary questions`}
          </p>
        </div>
        <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
          {items.length > 0 && (
            <button style={s.btnDanger} onClick={handleClearAll}>Clear all</button>
          )}
          <button style={s.btnGhost} onClick={() => setAddItem({ ...BLANK_ITEM })}>+ Add manually</button>
          <button style={s.btnPrimary} onClick={() => fileRef.current.click()} disabled={scanning}>
            {scanning ? (
              <><span style={s.spinnerEl} />Scanning…</>
            ) : (
              <>
                <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ marginRight: 6 }}>
                  <path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z"/>
                  <circle cx="12" cy="13" r="4"/>
                </svg>
                Scan menu
              </>
            )}
          </button>
          <input ref={fileRef} type="file" accept="image/*" capture="environment"
            style={{ display: 'none' }} onChange={handleFileChange} />
        </div>
      </div>

      {error && (
        <div style={s.errorBanner}>
          {error}
          <button style={s.errorX} onClick={() => setError('')}>×</button>
        </div>
      )}

      {/* Empty state */}
      {items.length === 0 && (
        <div style={s.emptyState} onClick={() => fileRef.current.click()}>
          <div style={s.emptyIcon}>
            <svg width="36" height="36" viewBox="0 0 24 24" fill="none" stroke="#94a3b8" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round">
              <path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z"/>
              <circle cx="12" cy="13" r="4"/>
            </svg>
          </div>
          <div style={s.emptyTitle}>Scan your menu</div>
          <div style={s.emptySub}>Take a photo or upload an image of your physical menu. AI will read and extract every item automatically.</div>
          <div style={{ display: 'flex', gap: 10 }}>
            <button style={s.btnGhost} onClick={e => { e.stopPropagation(); setAddItem({ ...BLANK_ITEM }); }}>+ Add manually</button>
            <button style={s.btnPrimary} onClick={e => { e.stopPropagation(); fileRef.current.click(); }}>
              Upload menu image
            </button>
          </div>
        </div>
      )}

      {/* Category tabs + grid */}
      {items.length > 0 && (
        <>
          <div style={s.tabs}>
            {categories.map(cat => (
              <button key={cat}
                style={{ ...s.tab, ...(activeCategory === cat ? s.tabActive : {}) }}
                onClick={() => setActiveCategory(cat)}>
                {cat}
                <span style={s.tabCount}>
                  {cat === 'All' ? items.length : items.filter(i => i.category === cat).length}
                </span>
              </button>
            ))}
            <button style={s.scanAgainBtn} onClick={() => fileRef.current.click()}>+ Scan more</button>
          </div>

          <div style={s.grid}>
            {visible.map(item => (
              <div key={item.id} style={{ ...s.card, opacity: item.is_available ? 1 : 0.55 }}>
                <div style={s.cardBody}>
                  <div style={s.cardTop}>
                    <span style={{ ...s.catTag, background: categoryColor(item.category) + '22', color: categoryColor(item.category) }}>
                      {item.category}
                    </span>
                    {!item.is_available && <span style={s.unavailableBadge}>Unavailable</span>}
                  </div>
                  <div style={s.itemName}>{item.name}</div>
                  {item.price ? <div style={s.itemPrice}>{item.price}</div> : null}
                  {item.description ? <div style={s.itemDesc}>{item.description}</div> : null}
                </div>
                <div style={s.cardActions}>
                  <button style={s.actionBtn} onClick={() => setEditItem({ ...item })}>Edit</button>
                  <button
                    style={{ ...s.actionBtn, color: item.is_available ? '#64748b' : '#4f46e5' }}
                    onClick={async () => { await api.updateMenuItem(item.id, { is_available: !item.is_available }); await load(); }}>
                    {item.is_available ? 'Mark unavailable' : 'Mark available'}
                  </button>
                  <button style={{ ...s.actionBtn, color: '#ef4444' }} onClick={() => setDeleteConfirm(item.id)}>Delete</button>
                </div>
              </div>
            ))}
          </div>
        </>
      )}

      {/* Add manually modal */}
      {addItem && (
        <div style={s.overlay} onClick={() => setAddItem(null)}>
          <div style={s.modal} onClick={e => e.stopPropagation()}>
            <div style={s.modalHeader}>
              <h2 style={s.modalTitle}>Add item</h2>
              <button style={s.xBtn} onClick={() => setAddItem(null)}>×</button>
            </div>
            <div style={s.field}><label style={s.label}>Name *</label>
              <input style={s.input} value={addItem.name} autoFocus
                onChange={e => setAddItem(p => ({ ...p, name: e.target.value }))} placeholder="e.g. Spaghetti Carbonara" /></div>
            <div style={s.field}><label style={s.label}>Category</label>
              <input style={s.input} value={addItem.category} list="existing-cats"
                onChange={e => setAddItem(p => ({ ...p, category: e.target.value }))} placeholder="e.g. Pasta" />
              <datalist id="existing-cats">
                {Array.from(new Set(items.map(i => i.category))).map(c => <option key={c} value={c} />)}
              </datalist>
            </div>
            <div style={s.field}><label style={s.label}>Price</label>
              <input style={s.input} value={addItem.price}
                onChange={e => setAddItem(p => ({ ...p, price: e.target.value }))} placeholder="e.g. £12.50" /></div>
            <div style={s.field}><label style={s.label}>Description</label>
              <textarea style={{ ...s.input, resize: 'vertical', minHeight: 72 }} value={addItem.description}
                onChange={e => setAddItem(p => ({ ...p, description: e.target.value }))} placeholder="Optional" /></div>
            <div style={s.modalFooter}>
              <button style={s.btnGhost} onClick={() => setAddItem(null)}>Cancel</button>
              <button style={s.btnPrimary} onClick={handleSaveAdd} disabled={!addItem.name.trim()}>Add item</button>
            </div>
          </div>
        </div>
      )}

      {/* Edit modal */}
      {editItem && (
        <div style={s.overlay} onClick={() => setEditItem(null)}>
          <div style={s.modal} onClick={e => e.stopPropagation()}>
            <div style={s.modalHeader}>
              <h2 style={s.modalTitle}>Edit item</h2>
              <button style={s.xBtn} onClick={() => setEditItem(null)}>×</button>
            </div>
            <div style={s.field}><label style={s.label}>Name</label>
              <input style={s.input} value={editItem.name} onChange={e => setEditItem(p => ({ ...p, name: e.target.value }))} /></div>
            <div style={s.field}><label style={s.label}>Category</label>
              <input style={s.input} value={editItem.category} onChange={e => setEditItem(p => ({ ...p, category: e.target.value }))} /></div>
            <div style={s.field}><label style={s.label}>Price</label>
              <input style={s.input} value={editItem.price} onChange={e => setEditItem(p => ({ ...p, price: e.target.value }))} placeholder="e.g. £12.50" /></div>
            <div style={s.field}><label style={s.label}>Description</label>
              <textarea style={{ ...s.input, resize: 'vertical', minHeight: 80 }} value={editItem.description}
                onChange={e => setEditItem(p => ({ ...p, description: e.target.value }))} /></div>
            <label style={s.checkRow}>
              <input type="checkbox" checked={!!editItem.is_available}
                onChange={e => setEditItem(p => ({ ...p, is_available: e.target.checked }))} />
              <span style={{ fontSize: 13, color: '#475569' }}>Available</span>
            </label>
            <div style={s.modalFooter}>
              <button style={s.btnGhost} onClick={() => setEditItem(null)}>Cancel</button>
              <button style={s.btnPrimary} onClick={handleSaveEdit}>Save changes</button>
            </div>
          </div>
        </div>
      )}

      {deleteConfirm && (
        <ConfirmModal
          message="Delete this menu item?"
          detail="The AI will no longer reference it for dietary questions."
          confirmLabel="Delete"
          onConfirm={() => handleDelete(deleteConfirm)}
          onCancel={() => setDeleteConfirm(null)}
        />
      )}

      {clearConfirm && (
        <ConfirmModal
          message="Delete all menu items?"
          detail="This cannot be undone."
          confirmLabel="Delete all"
          onConfirm={doClearAll}
          onCancel={() => setClearConfirm(false)}
        />
      )}
    </div>
  );
}

const s = {
  page:        { paddingBottom: 40 },
  header:      { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 24, gap: 16, flexWrap: 'wrap' },
  title:       { fontSize: 22, fontWeight: 800, color: '#0f172a', margin: 0, letterSpacing: '-0.4px' },
  sub:         { fontSize: 13, color: '#64748b', margin: '4px 0 0' },

  btnPrimary:  { display: 'flex', alignItems: 'center', background: '#4f46e5', color: '#fff', border: 'none', borderRadius: 9, padding: '9px 18px', fontSize: 13, fontWeight: 600, cursor: 'pointer', gap: 4, whiteSpace: 'nowrap' },
  btnGhost:    { background: 'none', color: '#64748b', border: '1px solid #e2e8f0', borderRadius: 9, padding: '9px 16px', fontSize: 13, fontWeight: 500, cursor: 'pointer' },
  btnDanger:   { background: 'none', color: '#ef4444', border: '1px solid #fca5a5', borderRadius: 9, padding: '9px 16px', fontSize: 13, fontWeight: 500, cursor: 'pointer' },
  spinnerEl:   { display: 'inline-block', width: 12, height: 12, border: '2px solid rgba(255,255,255,0.35)', borderTopColor: '#fff', borderRadius: '50%', marginRight: 8, animation: 'spin 0.7s linear infinite' },

  errorBanner: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 10, padding: '10px 14px', fontSize: 13, color: '#dc2626', marginBottom: 20 },
  errorX:      { background: 'none', border: 'none', color: '#dc2626', fontSize: 18, cursor: 'pointer', padding: 0, lineHeight: 1 },

  emptyState:  { border: '2px dashed #e2e8f0', borderRadius: 16, padding: '64px 24px', textAlign: 'center', cursor: 'pointer', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 14 },
  emptyIcon:   { width: 72, height: 72, background: '#f1f5f9', borderRadius: 18, display: 'flex', alignItems: 'center', justifyContent: 'center' },
  emptyTitle:  { fontSize: 17, fontWeight: 700, color: '#1e293b' },
  emptySub:    { fontSize: 13, color: '#94a3b8', maxWidth: 340, lineHeight: 1.6 },
  empty:       { color: '#94a3b8', fontSize: 14, padding: '48px 0', textAlign: 'center' },

  previewImg:  { maxWidth: '100%', maxHeight: 220, borderRadius: 12, objectFit: 'contain', marginBottom: 20, border: '1px solid #e2e8f0' },
  reviewList:  { display: 'flex', flexDirection: 'column', gap: 8 },
  reviewRow:   { display: 'flex', alignItems: 'center', gap: 8, background: '#fff', border: '1px solid #e2e8f0', borderRadius: 10, padding: '10px 12px' },
  reviewFields:{ display: 'flex', gap: 8, flex: 1, flexWrap: 'wrap' },
  reviewInput: { flex: 1, minWidth: 100, background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 7, padding: '6px 10px', fontSize: 13, color: '#0f172a', outline: 'none' },
  removeBtn:   { background: 'none', border: 'none', color: '#94a3b8', fontSize: 20, cursor: 'pointer', lineHeight: 1, padding: '0 4px', flexShrink: 0 },

  tabs:        { display: 'flex', gap: 6, marginBottom: 20, flexWrap: 'wrap', alignItems: 'center' },
  tab:         { background: '#f1f5f9', border: 'none', borderRadius: 8, padding: '7px 14px', fontSize: 13, fontWeight: 500, color: '#64748b', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6 },
  tabActive:   { background: '#4f46e5', color: '#fff' },
  tabCount:    { fontSize: 11, fontWeight: 700, opacity: 0.7 },
  scanAgainBtn:{ marginLeft: 'auto', background: 'none', border: '1px dashed #cbd5e1', borderRadius: 8, padding: '7px 14px', fontSize: 13, color: '#94a3b8', cursor: 'pointer' },

  grid:        { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))', gap: 14 },
  card:        { background: '#fff', border: '1px solid #e2e8f0', borderRadius: 12, overflow: 'hidden', display: 'flex', flexDirection: 'column' },
  cardBody:    { padding: '14px 16px', flex: 1 },
  cardTop:     { display: 'flex', alignItems: 'center', gap: 6, marginBottom: 8, flexWrap: 'wrap' },
  catTag:      { fontSize: 11, fontWeight: 700, borderRadius: 6, padding: '3px 8px', letterSpacing: '0.03em' },
  unavailableBadge: { fontSize: 11, fontWeight: 600, background: '#fef9c3', color: '#92400e', borderRadius: 6, padding: '3px 8px' },
  itemName:    { fontSize: 15, fontWeight: 700, color: '#0f172a', marginBottom: 2 },
  itemPrice:   { fontSize: 13, fontWeight: 600, color: '#4f46e5', marginBottom: 4 },
  itemDesc:    { fontSize: 12, color: '#64748b', lineHeight: 1.4, display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' },
  cardActions: { display: 'flex', borderTop: '1px solid #f1f5f9', padding: '8px 12px', gap: 4 },
  actionBtn:   { background: 'none', border: 'none', color: '#64748b', fontSize: 12, fontWeight: 500, cursor: 'pointer', padding: '4px 6px', borderRadius: 6 },

  overlay:     { position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.4)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: 20 },
  modal:       { background: '#fff', borderRadius: 16, padding: '28px 28px 24px', maxWidth: 480, width: '100%', boxShadow: '0 20px 60px rgba(15,23,42,0.2)' },
  modalHeader: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 },
  modalTitle:  { fontSize: 16, fontWeight: 700, color: '#0f172a', margin: 0 },
  xBtn:        { background: 'none', border: 'none', fontSize: 22, color: '#94a3b8', cursor: 'pointer', lineHeight: 1, padding: 0 },
  modalFooter: { display: 'flex', gap: 10, justifyContent: 'flex-end', marginTop: 20 },
  field:       { marginBottom: 14 },
  label:       { display: 'block', fontSize: 11, fontWeight: 700, color: '#64748b', marginBottom: 5, textTransform: 'uppercase', letterSpacing: '0.06em' },
  input:       { width: '100%', background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 9, padding: '10px 13px', fontSize: 14, color: '#0f172a', outline: 'none', boxSizing: 'border-box' },
  checkRow:    { display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', marginBottom: 4 },
};
